// The diary editor's content field — toolbar, Tiptap surface, character count.
//
// W54.0 A1 moved it out of `components/diary/DiaryEditor.tsx` when that file
// was split to get under the `AGENTS.md §3 #10` ceiling.
//
// The counter is measured on the editor's own text: Tiptap's `CharacterCount`
// extension is not a dependency of this project, so `editor.storage
// .characterCount` is `undefined` and `getText().length` is the real
// measurement. Counting is not limiting — the limit itself is enforced in the
// editor's submit handler, and the counter is the label for it.

import { EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/react";

import { DiaryToolbar } from "@/components/diary/DiaryToolbar";

export interface DiaryContentFieldProps {
  editor: Editor | null;
  contentLength: number;
  limit: number;
  busy?: boolean;
}

export function DiaryContentField({
  editor,
  contentLength,
  limit,
  busy,
}: DiaryContentFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-label text-ink-2">內容</label>
      <div className="rounded-md border border-hairline bg-paper overflow-hidden focus-within:outline focus-within:outline-2 focus-within:outline-you-strong focus-within:outline-offset-2">
        {editor && <DiaryToolbar editor={editor} disabled={busy} />}
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none px-3 py-2.5 min-h-[300px] text-ink-1 [&_h1]:text-xl [&_h1]:font-display [&_h2]:text-lg [&_h2]:font-display [&_h3]:text-base [&_h3]:font-display [&_p]:text-ink-1 [&_ul]:list-disc [&_ol]:list-decimal [&_blockquote]:border-l-2 [&_blockquote]:border-ink-3 [&_blockquote]:pl-3 [&_code]:text-sm [&_code]:bg-ink-1/5 [&_pre]:bg-ink-1/5 [&_pre]:p-2"
        />
      </div>
      <p className="text-meta text-ink-3" data-testid="diary-content-count">
        {contentLength.toLocaleString("en-US")} /{" "}
        <span data-testid="diary-content-limit">{limit.toLocaleString("en-US")}</span>
      </p>
      {contentLength > limit ? (
        <p className="text-meta text-danger" data-testid="diary-content-over">
          超出 {limit.toLocaleString("en-US")} 字, 請刪減後再儲存
        </p>
      ) : null}
    </div>
  );
}
